import { reactive, computed } from "vue";
import * as T from '@/catalog-app/types';


const store = {

    debug: false,

    state: reactive({
        // --- configuration data
        mode: 'model',
        endpoints: {} as T.StringDict,
        // --- route/navigation data
        catalog: null as T.Catalog|null,
        brand: null as T.Category|null,
        category: null as T.Category|null,
        subcategories: [] as T.Category[],
        // --- page data
        count: 0,
        page: 1,
        pages: 1,
        // --- cards and filters
        cards: [] as T.Card[],
        filters: [] as T.AnyFilter[],
        selected: {} as T.SelectionRecord,
        promos: [] as T.Promo[],
    }),

    /* Tip: You should never replace the original state object in your actions - the components and the store need to
    share reference to the same object in order for mutations to be observed.
    https://v3.vuejs.org/guide/state-management.html#simple-state-management-from-scratch */

    // --- GETTERS --- --- --- --- --- --- --- --- --- --- --- --- --- ---

    /** Returns non-blank selections. (not [] '' null or undefined) */
    getSelections: computed((): T.SelectionRecord => {
        const selections = {} as T.SelectionRecord;
        for (const key of Object.keys(store.state.selected)) {
            const value = store.state.selected[key];
            if(value) {
                if (Array.isArray(value) && value.length >= 1) {
                    if (value[0] === null && value[1] === null) {
                        continue;
                    } if (key === 'hours' || key === 'year') {
                        selections[key + '_0'] = value[0];
                        selections[key + '_1'] = value[1];
                    } else {
                        selections[key] = value;
                    }
                } else if (typeof value === 'string' && value !== '') {
                    selections[key] = value;
                }
            }
        }
        return selections;
    }),

    // --- MUTATIONS --- --- --- --- --- --- --- --- --- --- --- --- --- ---

    initialize(data: any) {

        if (this.debug) {
            console.log('--- MUT: initialize ---')
            console.log(data);
        }
        // configuration data
        if (data.mode) this.state.mode = data.mode;
        if (data.endpoints) this.state.endpoints = data.endpoints;
        // route/navigation data
        if (data.catalog) this.state.catalog = data.catalog;
        if (data.brand) this.state.brand = data.brand;
        if (data.category) this.state.category = data.category;
        if (data.subcategories) this.state.subcategories = data.subcategories;
        // page data
        if (data.count) this.state.count = data.count;
        if (data.page) this.state.page = data.page;
        if (data.pages) this.state.pages = data.pages;
        // filters and cards
        if (data.filters) this.state.filters = data.filters;
        if (data.selected) this.setSelection(data.selected);
        if (data.cards) this.setCards(data.cards);
        if (data.promos) this.state.promos = data.promos;
    },

    update(data: any) {
        // configuration data
        // if (data.mode) this.state.mode = data.mode;
        // if (data.endpoints) this.state.endpoints = data.endpoints;
        // route/navigation data
        if (data.catalog) this.state.catalog = data.catalog;
        if (data.brand) this.state.brand = data.brand;
        if (data.category) this.state.category = data.category;
        if (data.subcategories) this.state.subcategories = data.subcategories;
        // page data
        if (data.count) this.state.count = data.count;
        if (data.page) this.state.page = data.page;
        if (data.pages) this.state.pages = data.pages;
        // cards and filters
        if (data.filters) this.state.filters = data.filters;
        // if (data.selected) this.setSelection(data.selected);

        // replace all cards if this is the first page, append otherwise
        if (data.cards) {
            if (this.state.page == 1) {
                this.setCards(data.cards);
            } else {
                this.appendCards(data.cards);
            }
        }

        if (data.promos) this.state.promos = data.promos;
    },

    clearSelection() {
        this.state.selected = {};
    },

    /** Sets the entire selection state */
    setSelection(value: any) {

        if (this.debug) {
            console.log('--- MUT: setSelection ---')
            console.log(value);
        }

        if (typeof value === 'object') {
            // iterate through object keys
            for (const name of Object.keys(value)) {
                const sel = value[name];
                if (!T.isNonEmpty(sel)) {
                    continue;
                }
                // find matching filter
                const filter = this.state.filters.find(x => x.name === name);
                if (filter) {
                    switch (filter.type) {
                        case 'single':
                            this.setSelectionSingle(name, sel);
                            break;
                        case 'multi':
                            this.setSelectionMulti(name, sel);
                            break;
                        case 'minmax':
                            this.setSelectionMinMax(name, sel);
                            break;
                        default:
                            console.log(`ERROR: no handler for filter type "${filter.type}" with name "${name}"...`);
                            continue;
                    }
                } else {
                    console.log(`ERROR: no matching filter with name "${name}"...`);
                    continue;
                }
            }
        }
    },

    // setPromos(promos: T.Promo[]) {
    //     this.state.promos = [];
    //     for (const p of promos) {
    //         this.state.promos.push(
    //             {
    //                 id: p.id,
    //                 title: p.title,
    //                 url: p.url,
    //                 img: p.img,
    //                 valid: new Date(p.date_valid),
    //                 expires: p.date_expires ? new Date(p.date_expires) : null,
    //             }
    //         )
    //     }
    // },

    setCards(cards: T.Card[]) {
        this.state.cards = cards;
    },

    appendCards(cards: T.Card[]) {
        this.state.cards.push(...cards);
    },

    setSelectionAny(name: string, value: T.AnySelection) {
        this.state.selected[name] = value;
    },

    setSelectionSingle(name: string, value: T.SingleSelection) {
        this.state.selected[name] = value;
    },

    setSelectionMulti(name: string, value: T.MultiSelection) {
        if (T.isMultiSelection(value)) {
            this.state.selected[name] = value;
            this.state.page = 1;
        } else if (typeof value === 'string') {
            this.state.selected[name] = [value];
            this.state.page = 1;
        } else {
            console.log(`Invalid MultiSelection value for ${name}: ${value}`);
        }
    },

    setSelectionMinMax(name: string, value: Array<number|string|null>) {

        if (this.debug) {
            console.log('setting minmax min: ', value[0]);
            console.log('setting minmax max: ', value[1]);
        }

        // attempt to coerce incoming array into numbers
        const parsedValue = value.map(x => {
            if (typeof x === 'string')
                return parseFloat(x);
            else
                return x
        }) as T.MinMaxSelection;

        if (T.isMinMaxSelection(parsedValue)) {
            this.state.selected[name] = parsedValue;
            this.state.page = 1;
        } else {
            console.log(`Invalid MinMaxSelection value for ${name}: ${parsedValue}`);
        }
    },

};

export const useStore = () => {
    return store;
}